import { Link2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import type { VideoNoticeVideoNoticeCollectionReadModel } from '@r/api/generated/client.schemas.ts';
import { Badge } from '@ui/badge.tsx';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle
} from '@ui/dialog.tsx';

interface VideoNoticeViewerDialogProps {
  videoNotice: VideoNoticeVideoNoticeCollectionReadModel | null;
  onOpenChange: (open: boolean) => void;
}

/**
 * Ouvre la notice sélectionnée dans un lecteur vidéo et affiche sa catégorie et sa description.
 */
export function VideoNoticeViewerDialog({
  videoNotice,
  onOpenChange
}: VideoNoticeViewerDialogProps) {
  const { t } = useTranslation();

  const handleCopyVideoUrl = (videoUrl: string) => {
    navigator.clipboard
      .writeText(videoUrl)
      .then(() => toast.success(t('common.clipboard.success')))
      .catch(() => toast.error(t('common.clipboard.error')));
  };

  return (
    <Dialog open={videoNotice !== null} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto p-0">
        {videoNotice && (
          <>
            <DialogHeader className="px-8 pt-8 pb-5">
              {videoNotice.category && (
                <Badge variant="secondary" className="mb-2 font-normal w-fit">
                  {videoNotice.category}
                </Badge>
              )}
              <DialogTitle className="pr-8 text-2xl">{videoNotice.title}</DialogTitle>
              <DialogDescription className="sr-only">
                {t('admin.video.notices.viewer.description')}
              </DialogDescription>
            </DialogHeader>

            {/* biome-ignore lint/a11y/useMediaCaption: l’API ne fournit pas encore de piste de sous-titres. */}
            <video
              src={videoNotice.videoUrl}
              controls
              playsInline
              preload="metadata"
              className="w-full bg-black aspect-video"
            />

            <div className="px-8 pt-5 pb-8 space-y-5">
              {videoNotice.description && (
                <div className="space-y-2">
                  <h3 className="text-xs font-semibold tracking-widest uppercase text-muted-foreground">
                    {t('admin.video.notices.viewer.about')}
                  </h3>
                  <p className="text-sm leading-relaxed whitespace-pre-wrap text-muted-foreground">
                    {videoNotice.description}
                  </p>
                </div>
              )}

              <div className="space-y-2">
                <h3 className="text-xs font-semibold tracking-widest uppercase text-muted-foreground">
                  {t('admin.video.notices.viewer.share')}
                </h3>
                <button
                  type="button"
                  onClick={() => handleCopyVideoUrl(videoNotice.videoUrl)}
                  className="flex gap-3 items-center px-4 py-3 w-full text-sm text-left rounded-lg transition-colors bg-muted hover:bg-muted/80"
                  aria-label={t('admin.video.notices.viewer.sharecopyaria', {
                    title: videoNotice.title
                  })}
                >
                  <span className="flex-1 min-w-0 truncate">
                    {t('admin.video.notices.viewer.sharelinklabel', {
                      title: videoNotice.title
                    })}
                  </span>
                  <Link2 className="size-4 shrink-0 text-muted-foreground" />
                </button>
              </div>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
